"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { cn } from "@/lib/_utils";
import { HERO_SLIDES } from "./data";

export function HomeV2Hero() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  return (
    <section className="px-4 pt-4 pb-12 sm:px-6 lg:px-8">
      <Carousel
        className="mx-auto max-w-[1400px]"
        opts={{ loop: true }}
        setApi={setApi}
      >
        <CarouselContent className="-ml-0">
          {HERO_SLIDES.map((slide) => (
            <CarouselItem className="pl-0" key={slide.id}>
              {/* Figma: 1400×710, padding 80, radius 50 */}
              <div className="tt-gradient relative min-h-[420px] overflow-hidden rounded-[2rem] sm:min-h-[520px] sm:rounded-[2.5rem] lg:min-h-[710px] lg:rounded-[50px]">
                <div className="relative z-10 grid lg:min-h-[710px] lg:grid-cols-[560px_1fr] lg:items-start lg:gap-[143px] lg:p-20">
                  <div className="relative z-10 flex max-w-[560px] flex-col px-6 py-10 text-white sm:px-10 sm:py-12 lg:px-0 lg:py-0">
                    {/* Eyebrow — 20px / 700 / tracking 0.2em */}
                    <p className="font-bold text-xl uppercase leading-none tracking-[0.2em]">
                      {slide.label}
                    </p>

                    {/* Spacing 8px */}
                    <div aria-hidden className="h-2" />

                    {/* Headline — 72px / 700 / lh 110% / tracking -0.02em */}
                    <h1 className="font-bold text-4xl leading-[1.1] tracking-[-0.02em] sm:text-5xl lg:text-7xl">
                      {slide.headline}
                    </h1>

                    {/* Spacing 24px */}
                    <div aria-hidden className="h-6" />

                    {/* Body — 20px / 400 / lh 140% */}
                    <p className="font-normal text-white text-xl leading-[1.4]">
                      {slide.description}
                    </p>

                    {/* Spacing 40px */}
                    <div aria-hidden className="h-10" />

                    {/* CTAs — h-45, pill, 18px/600, gap 19px */}
                    <div className="flex flex-wrap items-center gap-[19px]">
                      <Button
                        asChild
                        className="h-[45px] min-w-[130px] rounded-full bg-tt-yellow px-6 font-semibold text-[#2C2C2C] text-lg leading-[1.4] hover:bg-tt-yellow/90"
                      >
                        <Link href={slide.primaryCta.href}>
                          {slide.primaryCta.label}
                        </Link>
                      </Button>
                      <Button
                        asChild
                        className="h-[45px] min-w-[130px] rounded-full border-2 border-tt-yellow bg-transparent px-6 font-semibold text-lg text-tt-yellow leading-[1.4] hover:bg-tt-yellow/10"
                        variant="outline"
                      >
                        <Link href={slide.secondaryCta.href}>
                          {slide.secondaryCta.label}
                        </Link>
                      </Button>
                    </div>

                    {/* Spacing 64px then indicators (desktop flow) */}
                    <div aria-hidden className="hidden h-16 lg:block" />
                    <div className="mt-8 flex gap-2 p-4 lg:mt-0 lg:p-0">
                      {HERO_SLIDES.map((dot, index) => (
                        <button
                          aria-label={`Go to slide ${index + 1}`}
                          className={cn(
                            "size-2 rounded-full transition-colors",
                            current === index
                              ? "bg-tt-yellow"
                              : "bg-[#C4C4C4] hover:bg-white/80"
                          )}
                          key={dot.id}
                          onClick={() => api?.scrollTo(index)}
                          type="button"
                        />
                      ))}
                    </div>
                  </div>

                  {/* Spacer on mobile; desktop image is absolute */}
                  <div className="relative h-[340px] sm:h-[400px] lg:h-auto" />
                </div>

                {/* Image cropped by hero overflow */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[110%] sm:h-[115%] lg:inset-y-0 lg:right-0 lg:left-[560px] lg:h-auto lg:w-auto">
                  <div className="absolute inset-x-[-8%] top-0 bottom-0 lg:inset-x-[-5%] lg:top-[-12%] lg:bottom-[-8%]">
                    <Image
                      alt={slide.imageAlt}
                      className={slide.imageClassName}
                      fill
                      priority={slide.id === "diyer"}
                      sizes="(max-width: 1024px) 100vw, 55vw"
                      src={slide.image}
                    />
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </section>
  );
}
